import React from "react";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorId: string;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error | null; errorInfo: React.ErrorInfo | null; errorId: string }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo, errorId: string) => void;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: ''
    };
  }

  // if an error happened, set the state to true
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    console.error('ErrorBoundary caught an error:', error);
    return { 
      hasError: true, 
      error,
      errorId: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Update state with error info
    this.setState({ errorInfo });

    // Log detailed error information
    console.group(`🚨 Error Boundary Error (${this.state.errorId})`);
    console.error('Error:', error);
    console.error('Error name:', error.name);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    console.error('Component stack:', errorInfo.componentStack);
    console.error('Error info:', errorInfo);
    console.groupEnd();

    // Send error to external service if provided
    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo, this.state.errorId);
      } catch (reportingError) {
        console.error('Failed to report error:', reportingError);
      }
    }

    // Additional production logging
    this.logErrorToServer(error, errorInfo);
  }

  private logErrorToServer = async (error: Error, errorInfo: React.ErrorInfo) => {
    try {
      const errorData = {
        errorId: this.state.errorId,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
        componentStack: errorInfo.componentStack,
        // Add any additional context you want to capture
        context: {
          // You can add user info, app state, etc.
        }
      };

      // Send to your error reporting service
      // Example: await fetch('/api/errors', { method: 'POST', body: JSON.stringify(errorData) });
      console.log('Error data for reporting:', errorData);
    } catch (loggingError) {
      console.error('Failed to log error to server:', loggingError);
    }
  };

  render() {
    // if error happened, return a fallback component
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return (
        <FallbackComponent 
          error={this.state.error} 
          errorInfo={this.state.errorInfo} 
          errorId={this.state.errorId}
        />
      );
    }

    return this.props.children;
  }
}

// Default error fallback component
const DefaultErrorFallback: React.FC<{ 
  error: Error | null; 
  errorInfo: React.ErrorInfo | null; 
  errorId: string;
}> = ({ error, errorInfo, errorId }) => {
  const [showDetails, setShowDetails] = React.useState(false);

  return (
    <div style={{
      padding: '20px',
      margin: '20px',
      border: '2px solid #ff6b6b',
      borderRadius: '8px',
      backgroundColor: '#fff5f5',
      fontFamily: 'monospace'
    }}>
      <h2 style={{ color: '#d63031', margin: '0 0 10px 0' }}>
        🚨 Something went wrong
      </h2>
      
      <p style={{ margin: '10px 0', color: '#2d3436' }}>
        An error occurred in the application. Error ID: <strong>{errorId}</strong>
      </p>

      {error && (
        <div style={{ margin: '10px 0' }}>
          <p><strong>Error:</strong> {error.name}: {error.message}</p>
        </div>
      )}

      <button
        onClick={() => setShowDetails(!showDetails)}
        style={{
          background: '#74b9ff',
          color: 'white',
          border: 'none',
          padding: '8px 16px',
          borderRadius: '4px',
          cursor: 'pointer',
          margin: '10px 0'
        }}
      >
        {showDetails ? 'Hide Details' : 'Show Details'}
      </button>

      {showDetails && (
        <div style={{ 
          marginTop: '15px',
          padding: '10px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          fontSize: '12px',
          maxHeight: '300px',
          overflow: 'auto'
        }}>
          {error && (
            <div>
              <h4>Error Stack:</h4>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {error.stack}
              </pre>
            </div>
          )}
          
          {errorInfo && (
            <div>
              <h4>Component Stack:</h4>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {errorInfo.componentStack}
              </pre>
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: '15px', fontSize: '12px', color: '#636e72' }}>
        <p>Please report this error with ID: <code>{errorId}</code></p>
        <p>Timestamp: {new Date().toISOString()}</p>
      </div>
    </div>
  );
};
